import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import officeImage from '../assets/day-office-travel-agency_23-2150769936.jpg_ijmk2n.jpg';

const ClientSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      name: "John A.",
      title: "Business Owner",
      quote: "No matter the time zone, Segway's team has always been very accessible and has helped me understand their unique products, helping me in my decision-making process."
    },
    {
      name: "Sarah M.",
      title: "Investment Manager",
      quote: "Segway's platform has transformed how we manage our digital assets. The security and ease of use are unmatched in the industry."
    },
    {
      name: "Michael R.",
      title: "Tech Entrepreneur",
      quote: "The personalized service and innovative solutions from Segway have been crucial to our company's growth strategy."
    }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="section-container">
        {/* Hero Banner with Testimonial Overlay */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 1.2, 
            ease: [0.25, 0.46, 0.45, 0.94],
            delay: 0.2 
          }}
          viewport={{ once: true, margin: "-100px" }}
          className="relative rounded-3xl overflow-hidden"
        >
          {/* Background Image */}
          <motion.div
            initial={{ opacity: 0, scale: 1.1 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="absolute inset-0"
          >
            <img 
              src={officeImage} 
              alt="Modern office environment"
              className="w-full h-full object-cover"
            />
            {/* Dark overlay for text readability */}
            <div className="absolute inset-0 bg-black/60"></div>
          </motion.div>

          {/* Content Overlay */}
          <div className="relative z-10 p-8 lg:p-12 text-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[400px] lg:min-h-[500px]">
              {/* Left Content - Main Text */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                viewport={{ once: true }}
                className="max-w-2xl"
              >
                <motion.h2 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  viewport={{ once: true }}
                  className="text-3xl lg:text-4xl xl:text-5xl font-bold mb-6 leading-tight"
                >
                  Serving clients since
                  <br />
                  2018.
                </motion.h2>
                
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1 }}
                  viewport={{ once: true }}
                  className="text-lg lg:text-xl text-gray-200 leading-relaxed"
                >
                  Individuals, businesses, and institutions rely on us to help grow their digital asset portfolios.
                </motion.p>
              </motion.div>

              {/* Right Content - Testimonial Card */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 lg:p-8 text-gray-900 relative">
                  {/* Navigation Arrows */}
                  <div className="absolute -left-4 top-1/2 transform -translate-y-1/2 z-20">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={prevTestimonial}
                      className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors"
                    >
                      <ChevronLeftIcon className="w-5 h-5" />
                    </motion.button>
                  </div>

                  <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 z-20">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={nextTestimonial}
                      className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors"
                    >
                      <ChevronRightIcon className="w-5 h-5" />
                    </motion.button>
                  </div>

                  {/* Testimonial Content */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentTestimonial}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-4"
                    >
                      <div>
                        <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-1">
                          {testimonials[currentTestimonial].name}
                        </h3>
                        <p className="text-gray-600 font-medium text-sm lg:text-base">
                          {testimonials[currentTestimonial].title}
                        </p>
                      </div>

                      <blockquote className="text-base lg:text-lg text-gray-700 leading-relaxed">
                        <span className="text-3xl text-gray-300 font-serif leading-none mr-2">"</span>
                        {testimonials[currentTestimonial].quote}
                        <span className="text-3xl text-gray-300 font-serif leading-none ml-2">"</span>
                      </blockquote>
                    </motion.div>
                  </AnimatePresence>

                  {/* Dots Indicator */}
                  <div className="flex justify-center space-x-2 mt-6">
                    {testimonials.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentTestimonial(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentTestimonial ? 'bg-gray-700' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ClientSection; 